import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaAt,
  FaCheck,
  FaGlobe,
  FaImage,
  FaListOl,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSave,
  FaTimes,
  FaUserTie,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../../../services/partenairesPhotoServices";

import {
  PARTENAIRE_FALLBACK_IMAGE,
  PARTENAIRE_QUILL_FORMATS,
  PARTENAIRE_QUILL_MODULES,
  getDefaultPartenaireForm,
  mapPartenaireToForm,
} from "../utils/partenairesSettingsUtils";

function DrawerField({ label, icon, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        ) : null}

        {children}
      </div>

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function ToggleBox({ label, description, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition ${
        checked
          ? "border-indigo-200 bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-500/10"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-800"
      }`}
    >
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-xs ${
          checked
            ? "bg-indigo-600 text-white dark:bg-indigo-500"
            : "bg-slate-100 text-slate-400 dark:bg-slate-800"
        }`}
      >
        {checked ? <FaCheck /> : null}
      </span>

      <span>
        <span className="block text-sm font-bold text-slate-900 dark:text-white">
          {label}
        </span>

        {description ? (
          <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function ImagePreviewBlock({ label, value }) {
  const src = toAbsolutePhotoUrl(value) || PARTENAIRE_FALLBACK_IMAGE;

  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/70">
      <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        <FaImage />
        {label}
      </p>

      <div className="h-36 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
        <img
          src={src}
          alt={label}
          className="h-full w-full object-contain p-2"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          onError={(event) => {
            event.currentTarget.src = PARTENAIRE_FALLBACK_IMAGE;
          }}
        />
      </div>
    </div>
  );
}

export default function PartenaireFormDrawer({
  isOpen,
  mode,
  partenaire,
  nextOrder,
  submitting,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultPartenaireForm(nextOrder));

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit") {
      setFormData(mapPartenaireToForm(partenaire, nextOrder));
    } else {
      setFormData(getDefaultPartenaireForm(nextOrder));
    }
  }, [isOpen, mode, partenaire, nextOrder]);

  const title =
    mode === "edit" ? "Modifier le partenaire" : "Ajouter un partenaire";

  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Créer le partenaire";

  const canSubmit = String(formData.nom || "").trim() && !submitting;

  const logoPreview = useMemo(
    () => toAbsolutePhotoUrl(formData.photo_url) || PARTENAIRE_FALLBACK_IMAGE,
    [formData.photo_url]
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const setField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Partenaire
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez les informations principales. Les images peuvent être
              saisies par URL ici ou téléversées ensuite depuis le tableau.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-5 xl:grid-cols-[320px,1fr]">
              <div className="space-y-4">
                <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <div className="h-44 rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                    <img
                      src={logoPreview}
                      alt={formData.nom || "Logo partenaire"}
                      className="h-full w-full object-contain p-4"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      onError={(event) => {
                        event.currentTarget.src = PARTENAIRE_FALLBACK_IMAGE;
                      }}
                    />
                  </div>

                  <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
                    Aperçu du logo principal.
                  </p>
                </div>

                <ToggleBox
                  label="Afficher sur le site"
                  description="Le partenaire apparaît dans la section publique des partenaires."
                  checked={!!formData.is_active}
                  onChange={(value) => setField("is_active", value)}
                />

                <ToggleBox
                  label="Créer une page détail"
                  description="Active une page complète avec coordonnées, images et descriptions."
                  checked={!!formData.is_detail}
                  onChange={(value) => setField("is_detail", value)}
                />

                <DrawerField
                  label="Ordre d’affichage"
                  icon={<FaListOl />}
                  hint="Plus le nombre est petit, plus le partenaire apparaît tôt."
                >
                  <input
                    type="number"
                    name="ordre"
                    value={formData.ordre}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </DrawerField>
              </div>

              <div className="space-y-5">
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="lg:col-span-2">
                      <DrawerField
                        label="Nom du partenaire *"
                        icon={<FaUserTie />}
                      >
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          required
                          placeholder="Ex : Super Sponsor SAS"
                          className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        />
                      </DrawerField>
                    </div>

                    <DrawerField label="Site web" icon={<FaGlobe />}>
                      <input
                        type="url"
                        name="link_url"
                        value={formData.link_url}
                        onChange={handleChange}
                        placeholder="https://..."
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>

                    <DrawerField label="Téléphone" icon={<FaPhoneAlt />}>
                      <input
                        type="text"
                        name="tel"
                        value={formData.tel}
                        onChange={handleChange}
                        placeholder="05 56..."
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>

                    <DrawerField label="Email" icon={<FaAt />}>
                      <input
                        type="email"
                        name="Mail"
                        value={formData.Mail}
                        onChange={handleChange}
                        placeholder="contact@exemple.com"
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>

                    <DrawerField label="Adresse" icon={<FaMapMarkerAlt />}>
                      <input
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="12 rue des Sponsors, 33000 Bordeaux"
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
                  <p className="mb-4 text-sm font-bold text-slate-900 dark:text-white">
                    Images du partenaire
                  </p>

                  <div className="grid gap-4 lg:grid-cols-3">
                    <ImagePreviewBlock
                      label="Logo"
                      value={formData.photo_url}
                    />
                    <ImagePreviewBlock
                      label="Photo 2"
                      value={formData.photo2_url}
                    />
                    <ImagePreviewBlock
                      label="Photo 3"
                      value={formData.photo3_url}
                    />
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-3">
                    <DrawerField label="URL du logo">
                      <input
                        type="text"
                        name="photo_url"
                        value={formData.photo_url}
                        onChange={handleChange}
                        placeholder="https://... ou /uploads/..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>

                    <DrawerField label="URL photo 2">
                      <input
                        type="text"
                        name="photo2_url"
                        value={formData.photo2_url}
                        onChange={handleChange}
                        placeholder="https://... ou /uploads/..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>

                    <DrawerField label="URL photo 3">
                      <input
                        type="text"
                        name="photo3_url"
                        value={formData.photo3_url}
                        onChange={handleChange}
                        placeholder="https://... ou /uploads/..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>
                  </div>
                </div>

                <div className="grid gap-5 xl:grid-cols-1">
                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
                    <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Description photo 2
                    </label>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
                      <ReactQuill
                        theme="snow"
                        value={formData.description2}
                        onChange={(value) => setField("description2", value)}
                        modules={PARTENAIRE_QUILL_MODULES}
                        formats={PARTENAIRE_QUILL_FORMATS}
                        placeholder="Texte associé à la photo 2..."
                      />
                    </div>
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
                    <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Description photo 3
                    </label>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
                      <ReactQuill
                        theme="snow"
                        value={formData.description3}
                        onChange={(value) => setField("description3", value)}
                        modules={PARTENAIRE_QUILL_MODULES}
                        formats={PARTENAIRE_QUILL_FORMATS}
                        placeholder="Texte associé à la photo 3..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>

        <style>{`
          .quill-wrapper .ql-toolbar.ql-snow {
            border: 0;
            border-bottom: 1px solid #e2e8f0;
            background: #ffffff;
          }

          .quill-wrapper .ql-container.ql-snow {
            border: 0;
            min-height: 170px;
          }

          .quill-wrapper .ql-editor {
            min-height: 170px;
            font-size: 14px;
            line-height: 1.7;
          }

          .dark .quill-wrapper .ql-toolbar.ql-snow {
            background: rgba(15,23,42,.98);
            border-color: #334155;
          }

          .dark .quill-wrapper .ql-container.ql-snow {
            background: rgba(2,6,23,.7);
          }

          .dark .quill-wrapper .ql-editor {
            color: #e2e8f0;
          }

          .dark .ql-snow .ql-stroke {
            stroke: #e2e8f0 !important;
          }

          .dark .ql-snow .ql-fill {
            fill: #e2e8f0 !important;
          }

          .dark .ql-snow .ql-picker,
          .dark .ql-snow .ql-picker-label,
          .dark .ql-snow .ql-picker-options,
          .dark .ql-snow .ql-toolbar button {
            color: #e2e8f0 !important;
          }

          .dark .ql-snow .ql-picker-options {
            background: #0b1220;
            border-color: #334155;
          }

          .dark .ql-editor strong,
          .dark .ql-editor b {
            color: #fff;
          }
        `}</style>
      </aside>
    </div>
  );
}